import React, { useState } from 'react';
import { ClockIcon, DollarSignIcon, TruckIcon, CalendarIcon, FileTextIcon, EyeIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, SearchIcon, FilterIcon } from 'lucide-react';
interface Document {
  type: string;
  fileName: string;
}
interface Request {
  id: string;
  truckingCompany: string;
  jobNumber: string;
  jobName: string;
  completionDate: string;
  requestDate: string;
  amount: number;
  invoiceNumber: string;
  status: string;
  priority: string;
  contractor: string;
  contractorRating: string;
  completedLoads: number;
  totalHours: number;
  documents: Document[];
  notes: string;
  paymentTerms: string;
  factorRate: number;
}
interface PendingRequestsProps {
  requests: Request[];
  onViewDetails: (requestId: string) => void;
}
export const PendingRequests: React.FC<PendingRequestsProps> = ({
  requests,
  onViewDetails
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('requestDate');
  const [sortDirection, setSortDirection] = useState('desc');
  // Filter and sort requests
  const filteredRequests = requests.filter(request => {
    const matchesSearch = searchTerm === '' || request.truckingCompany.toLowerCase().includes(searchTerm.toLowerCase()) || request.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) || request.jobName.toLowerCase().includes(searchTerm.toLowerCase()) || request.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || request.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'amount') {
      comparison = a.amount - b.amount;
    } else if (sortBy === 'requestDate') {
      comparison = new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime();
    } else if (sortBy === 'truckingCompany') {
      comparison = a.truckingCompany.localeCompare(b.truckingCompany);
    } else if (sortBy === 'priority') {
      const priorityOrder = {
        high: 0,
        medium: 1,
        low: 2
      };
      comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-3081">
      <div className="p-4 border-b" data-id="element-3082">
        <h2 className="text-lg font-medium flex items-center gap-2" data-id="element-3083">
          <ClockIcon size={20} className="text-[#0F2A52]" data-id="element-3084" />
          Pending Factoring Requests
        </h2>
      </div>
      {/* Filters */}
      <div className="p-4 border-b bg-gray-50" data-id="element-3085">
        <div className="flex flex-wrap gap-4" data-id="element-3086">
          <div className="flex-1 min-w-[200px]" data-id="element-3087">
            <div className="relative" data-id="element-3088">
              <input type="text" placeholder="Search by company, job, or invoice..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3089" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-3090" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-3091">
            <FilterIcon size={18} className="text-gray-500" data-id="element-3092" />
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" data-id="element-3093">
              <option value="all" data-id="element-3094">All Priorities</option>
              <option value="high" data-id="element-3095">High Priority</option>
              <option value="medium" data-id="element-3096">Medium Priority</option>
              <option value="low" data-id="element-3097">Low Priority</option>
            </select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto" data-id="element-3098">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-3099">
          <thead className="bg-gray-50" data-id="element-3100">
            <tr data-id="element-3101">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('truckingCompany')} data-id="element-3102">
                <div className="flex items-center gap-1" data-id="element-3103">
                  Company
                  {sortBy === 'truckingCompany' && <span data-id="element-3104">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3105">
                Job Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('requestDate')} data-id="element-3106">
                <div className="flex items-center gap-1" data-id="element-3107">
                  Date
                  {sortBy === 'requestDate' && <span data-id="element-3108">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-3109">
                <div className="flex items-center gap-1" data-id="element-3110">
                  Amount
                  {sortBy === 'amount' && <span data-id="element-3111">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('priority')} data-id="element-3112">
                <div className="flex items-center gap-1" data-id="element-3113">
                  Priority
                  {sortBy === 'priority' && <span data-id="element-3114">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3115">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3116">
            {filteredRequests.length > 0 ? filteredRequests.map(request => <tr key={request.id} className="hover:bg-gray-50" data-id="element-3117">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3118">
                    <div className="flex items-center" data-id="element-3119">
                      <div className="flex-shrink-0 h-10 w-10 bg-[#0F2A52] rounded-full flex items-center justify-center text-white" data-id="element-3120">
                        <TruckIcon size={20} data-id="element-3121" />
                      </div>
                      <div className="ml-4" data-id="element-3122">
                        <div className="text-sm font-medium text-gray-900" data-id="element-3123">
                          {request.truckingCompany}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-3124">
                          {request.invoiceNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-3125">
                    <div className="text-sm text-gray-900 font-medium" data-id="element-3126">
                      {request.jobName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-3127">
                      {request.jobNumber}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-3128">
                      Contractor: {request.contractor}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3129">
                    <div className="flex flex-col" data-id="element-3130">
                      <span className="text-sm text-gray-900 flex items-center gap-1" data-id="element-3131">
                        <FileTextIcon size={14} className="text-gray-400" data-id="element-3132" />
                        Request: {formatDate(request.requestDate)}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center gap-1" data-id="element-3133">
                        <CheckCircleIcon size={14} className="text-gray-400" data-id="element-3134" />
                        Completed: {formatDate(request.completionDate)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3135">
                    <div className="text-sm font-medium text-gray-900" data-id="element-3136">
                      $
                      {request.amount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3137">
                      {request.completedLoads} loads • {request.totalHours}{' '}
                      hours
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-3138">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadgeColor(request.priority)}`} data-id="element-3139">
                      {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3140">
                    <button onClick={() => onViewDetails(request.id)} className="text-[#0F2A52] hover:text-[#0F2A52]/80 bg-[#0F2A52]/10 hover:bg-[#0F2A52]/20 px-3 py-1 rounded-md flex items-center gap-1 ml-auto" data-id="element-3141">
                      <EyeIcon size={16} data-id="element-3142" />
                      Review
                    </button>
                  </td>
                </tr>) : <tr data-id="element-3143">
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500" data-id="element-3144">
                  No pending requests match your filters
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
};